import { inject, Injectable } from '@angular/core';

import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { IButtonConfig } from 'shared/components/atoms/button/button.component';
import { IPopupConfig, PopupHostComponent, StockPopupComponent } from 'shared/components/organisms';
import { ButtonColor, ButtonStyle, PopupWidth } from 'shared/enums';

@Injectable({
  providedIn: 'root',
})
export class PopupService {
  public readonly dialogSvc = inject(MatDialog);
  public readonly router = inject(Router);

  public dialogConfig = {
    width: PopupWidth.LARGE,
    maxWidth: 'none',
    ariaLabel: 'dialog-container',
    autoFocus: false,
  };

  public openPopup(config: MatDialogConfig<IPopupConfig>): MatDialogRef<PopupHostComponent> {
    this.removeActiveFocus();
    const dialogConfig = config.data?.width
      ? {
          width: config.data.width,
          maxWidth: 'none',
          ariaLabel: 'dialog-container',
        }
      : this.dialogConfig;

    return this.dialogSvc.open(PopupHostComponent, {
      ...dialogConfig,
      ...config,
    });
  }

  public alert(
    title: string,
    text: string,
    buttonText = 'OK',
    width = PopupWidth.SMALL,
  ): MatDialogRef<StockPopupComponent> {
    const dialogRef: MatDialogRef<StockPopupComponent> = this.dialogSvc.open(StockPopupComponent, {
      ...this.dialogConfig,
      width,
      panelClass: 'do-not-resize',
      data: {
        title,
        text,
        contentData: text,
        buttons: [
          {
            text: buttonText,
            style: ButtonStyle.OUTLINE,
            action: () => {
              dialogRef.close(true);
            },
          },
        ],
      },
    });
    return dialogRef;
  }

  public dialog(
    title: string,
    text: string,
    buttonConfig?: {
      okButton?: Partial<IButtonConfig>;
      cancelButton?: Partial<IButtonConfig>;
    },
    layoutConfig?: {
      footerClass?: string;
      showHeaderBorder?: boolean;
    },
  ): MatDialogRef<StockPopupComponent> {
    this.removeActiveFocus();
    const buttonCancelDefault: IButtonConfig = {
      style: ButtonStyle.OUTLINE,
      color: ButtonColor.PRIMARY,
      text: 'global.cancel',
      action: () => {
        dialogRef.close(false);
      },
    };

    const buttonOkDefault = {
      text: 'global.continue',
      color: ButtonColor.PRIMARY,
      action: () => {
        dialogRef.close(true);
      },
    };

    const dialogRef: MatDialogRef<StockPopupComponent> = this.dialogSvc.open(StockPopupComponent, {
      ...this.dialogConfig,
      width: PopupWidth.SMALL,
      panelClass: 'do-not-resize',
      data: {
        title,
        contentData: text,
        footerClass: layoutConfig?.footerClass,
        buttons: [
          {
            ...buttonCancelDefault,
            ...buttonConfig?.cancelButton,
          },
          {
            ...buttonOkDefault,
            ...buttonConfig?.okButton,
          },
        ],
        layoutConfig: {
          showHeaderBorder: false,
          ...layoutConfig,
        },
      },
    });
    return dialogRef;
  }

  private removeActiveFocus(): void {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }
}
